import React, { useContext, useMemo, useState } from 'react';
import { UserContext } from '../contexts/UserContext';
import { styled, keyframes } from '@mui/material/styles';
import {
  Box,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Divider,
  Snackbar,
} from '@mui/material';
import MailIcon from '@mui/icons-material/Mail';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import { getAuth } from 'firebase/auth';

// ---- Theme tokens (match Bazaar / global palette) ----
const COL = {
  bgStart: '#000000',
  bgEnd: '#0a0a0a',
  gold: '#d4af37',
  goldSoft: 'rgba(212,175,55,0.75)',
  goldDim: 'rgba(212,175,55,0.6)',
  text: '#f9f6e3',
  panel: 'rgba(0,0,0,0.70)',
};
const fonts = { title: `'Cinzel', serif` };
const glow = (s = 0.3) =>
  `0 0 24px rgba(212,175,55,${s}), 0 0 4px rgba(212,175,55,${s})`;

// ---- Gold pulse animation for ICON (not badge) ----
const iconPulse = keyframes`
  0% {
    box-shadow: 0 0 0 rgba(212,175,55,0.0);
  }
  50% {
    box-shadow:
      0 0 18px rgba(212,175,55,0.65),
      0 0 36px rgba(212,175,55,0.4);
  }
  100% {
    box-shadow: 0 0 0 rgba(212,175,55,0.0);
  }
`;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    background: 'rgba(0,0,0,0.85)',
    border: `1px solid ${COL.goldDim}`,
    color: COL.text,
    boxShadow: glow(0.35),
    borderRadius: 12,
  },
}));

const GoldDivider = styled(Divider)(() => ({
  borderColor: COL.goldDim,
  opacity: 0.6,
}));

const GoldButton = styled(Button)(({ theme }) => ({
  fontFamily: fonts.title,
  letterSpacing: '0.6px',
  fontWeight: 600,
  color: COL.bgEnd,
  background: `linear-gradient(180deg, ${COL.goldSoft}, ${COL.gold})`,
  border: `1px solid ${COL.gold}`,
  boxShadow: glow(0.25),
  textTransform: 'none',
  '&:hover': {
    boxShadow: glow(0.4),
    filter: 'brightness(1.05)',
    background: `linear-gradient(180deg, ${COL.gold}, ${COL.gold})`,
  },
  '&:disabled': {
    opacity: 0.5,
    color: '#3b3b3b',
    background: 'linear-gradient(180deg, #5f5630, #4b421d)',
  },
}));

const GhostButton = styled(Button)(({ theme }) => ({
  fontFamily: fonts.title,
  color: COL.text,
  border: `1px solid ${COL.goldDim}`,
  textTransform: 'none',
  background: 'transparent',
  '&:hover': {
    background: 'rgba(212,175,55,0.08)',
    boxShadow: glow(0.25),
  },
}));

// ---- Pulsing Mail IconButton (scaled smaller) ----
// Current: 1.27. "Scale down another 5%" => 1.27 * 0.95 = 1.2065 -> 1.21
const MailButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'pulse',
})(({ pulse }) => ({
  transform: 'scale(1.21)', // 🔍 scaled down ~5%
  borderRadius: 12,
  border: `1px solid ${COL.goldDim}`,
  background: 'rgba(0,0,0,0.7)',
  boxShadow: glow(0.15),
  ...(pulse && {
    animation: `${iconPulse} 1.8s ease-in-out infinite`,
  }),
  '&:hover': {
    boxShadow: glow(0.4),
    background: 'rgba(212,175,55,0.12)',
  },
}));

// ---- Badge for "unclaimed" notifications (number) ----
const UnclaimedBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#ff3b3b',
    color: '#fff',
    fontSize: 13,
    fontWeight: 900,
    minWidth: 22,
    height: 22,
    borderRadius: '50%',
    border: '2px solid #000',
    boxShadow: '0 0 8px rgba(255,59,59,0.95)',
    top: 6,
    right: 6,
  },
}));

const api = {
  async call(name, body) {
    const idToken = await getAuth().currentUser?.getIdToken();
    const url = `https://us-central1-beasts-of-legend-bc9bd.cloudfunctions.net/${name}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body || {}),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.error) {
      throw new Error(json?.error || 'error');
    }
    return json;
  },
  claimNotification(notificationId) {
    return api.call('claimNotification', { notificationId });
  },
};

// Safely turn Firestore timestamps / numbers into ms
function tsToMillis(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts === 'number') return ts;
  if (ts.seconds) return ts.seconds * 1000;
  return 0;
}

function formatDate(ts) {
  const ms = tsToMillis(ts);
  if (!ms) return '';
  return new Date(ms).toLocaleString();
}

// Describe arena reward contents (soul dust + tickets)
function describeArenaReward(rewardRaw) {
  const reward = rewardRaw || {};
  const parts = [];

  const sd = Number(reward.soulDust || 0);
  if (sd > 0) {
    parts.push(`${sd} Soul Dust`);
  }

  const tickets = reward.drawTickets || {};
  Object.entries(tickets).forEach(([type, amt]) => {
    const n = Number(amt || 0);
    if (!n) return;
    parts.push(`${n} ${type} ticket${n > 1 ? 's' : ''}`);
  });

  return parts.length ? parts.join(' + ') : 'Reward available';
}

export default function Notifications() {
  const { userData, setUserData } = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [claimingId, setClaimingId] = useState(null);

  const notifications = Array.isArray(userData?.notifications)
    ? userData.notifications
    : [];

  // Count UNCLAIMED notifications (this drives badge count + pulse)
  const unclaimedCount = notifications.filter((n) => !n?.claimed).length;
  const hasUnclaimed = unclaimedCount > 0;

  const sortedNotifications = useMemo(
    () =>
      [...notifications].sort(
        (a, b) => tsToMillis(b.createdAt) - tsToMillis(a.createdAt)
      ),
    [notifications]
  );

  const showSnackbar = (msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  const handleOpen = () => {
    setOpen(true);
    // Locally mark notifications as read (optional for any future UI)
    if (setUserData && notifications.length > 0) {
      setUserData((prev) => {
        if (!prev) return prev;
        const current = Array.isArray(prev.notifications)
          ? prev.notifications
          : [];
        const updated = current.map((n) => ({ ...n, read: true }));
        return { ...prev, notifications: updated };
      });
    }
  };

  const handleClose = () => setOpen(false);

  const handleClaim = async (notif) => {
    if (!notif || notif.claimed || !notif.id) return;
    setClaimingId(notif.id);
    try {
      const res = await api.claimNotification(notif.id);
      // Cloud function returns: { ok, notificationId, type, soulDust, drawTickets }
      const { type, soulDust, drawTickets } = res;

      setUserData((prev) => {
        if (!prev) return prev;

        const prevConsumables = { ...(prev.consumables || {}) };
        const prevDrawTickets = { ...(prev.drawTickets || {}) };

        // Update soulDust if provided
        if (typeof soulDust === 'number' && !Number.isNaN(soulDust)) {
          prevConsumables.soulDust = soulDust;
        }

        // Update drawTickets map if provided
        if (drawTickets && typeof drawTickets === 'object') {
          Object.entries(drawTickets).forEach(([k, v]) => {
            prevDrawTickets[k] = Number(v || 0);
          });
        }

        const currentNotifs = Array.isArray(prev.notifications)
          ? prev.notifications
          : [];
        const updatedNotifs = currentNotifs.map((n) =>
          n.id === notif.id ? { ...n, claimed: true, read: true } : n
        );

        return {
          ...prev,
          consumables: prevConsumables,
          drawTickets: prevDrawTickets,
          notifications: updatedNotifs,
        };
      });

      if (type === 'bazaarSale') {
        showSnackbar('Soul Dust from sale claimed!');
      } else if (type === 'arenaWinReward' || type === 'arenaRankReward') {
        showSnackbar('Arena reward claimed!');
      } else {
        showSnackbar('Reward claimed!');
      }
    } catch (e) {
      showSnackbar(e.message || 'Failed to claim reward');
    } finally {
      setClaimingId(null);
    }
  };

  return (
    <>
      {/* Mail icon w/ badge (UNCLAIMED count) */}
      <UnclaimedBadge
        overlap="circular"
        invisible={!hasUnclaimed}
        badgeContent={hasUnclaimed ? unclaimedCount : null}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MailButton size="large" onClick={handleOpen} pulse={hasUnclaimed}>
          <MailIcon sx={{ color: COL.gold, fontSize: 28 }} />
        </MailButton>
      </UnclaimedBadge>

      {/* Dialog */}
      <StyledDialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            fontFamily: fonts.title,
            letterSpacing: '0.8px',
            color: COL.text,
            textShadow: glow(0.25),
            pr: 5,
          }}
        >
          Notifications
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: COL.goldDim,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: COL.goldDim }}>
          {sortedNotifications.length === 0 && (
            <Typography
              variant="body2"
              sx={{
                fontFamily: fonts.title,
                color: COL.goldDim,
                textAlign: 'center',
                py: 2,
              }}
            >
              No notifications yet.
            </Typography>
          )}

          {sortedNotifications.map((notif, idx) => {
            const type = notif.type || '';
            const data = notif.data || {};
            const reward = data.reward || {};

            const isBazaarSale = type === 'bazaarSale';
            const isArenaWin = type === 'arenaWinReward';
            const isArenaRank = type === 'arenaRankReward';
            const isClaimed = !!notif.claimed;
            const isClaimable = isBazaarSale || isArenaWin || isArenaRank;

            const price = Number(data?.price || 0);
            const cardName = data?.cardName || data?.cardId;
            const winCount = Number(data.winCount || 0);
            const rank = Number(data.rank || 0);

            // Title
            let title = 'Notification';
            if (isBazaarSale) title = 'Bazaar Sale';
            else if (isArenaWin) title = 'Arena Win Reward';
            else if (isArenaRank) title = 'Arena Rank Reward';

            // Body message
            let body = notif.message || 'You have a new notification.';
            if (isBazaarSale) {
              body = `Your listing for ${cardName || 'a card'} was sold for ${price} Soul Dust.`;
            } else if (isArenaWin) {
              body = winCount
                ? `You reached ${winCount} arena wins in the current event.`
                : 'You earned an arena win reward.';
            } else if (isArenaRank) {
              body = rank
                ? `You finished the event at rank #${rank}.`
                : 'You earned an arena rank reward.';
            }

            const rewardSummary =
              isArenaWin || isArenaRank ? describeArenaReward(reward) : null;

            return (
              <Box key={notif.id || idx} sx={{ mb: 2.5 }}>
                {idx > 0 && <GoldDivider sx={{ mb: 2 }} />}

                <Typography
                  variant="subtitle2"
                  sx={{
                    fontFamily: fonts.title,
                    color: COL.gold,
                    letterSpacing: '0.6px',
                    mb: 0.5,
                  }}
                >
                  {title}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ fontFamily: fonts.title, mb: 0.5 }}
                >
                  {body}
                </Typography>

                {rewardSummary && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: fonts.title,
                      mb: 0.5,
                    }}
                  >
                    Reward:{' '}
                    <Box component="span" sx={{ color: COL.gold }}>
                      {rewardSummary}
                    </Box>
                  </Typography>
                )}

                {notif.createdAt && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontFamily: fonts.title,
                      color: COL.goldDim,
                      mb: 1,
                    }}
                  >
                    {formatDate(notif.createdAt)}
                  </Typography>
                )}

                {isClaimable ? (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <GoldButton
                      size="small"
                      onClick={() => handleClaim(notif)}
                      disabled={isClaimed || claimingId === notif.id}
                    >
                      {isClaimed
                        ? 'Claimed'
                        : claimingId === notif.id
                        ? 'Claiming...'
                        : isBazaarSale
                        ? 'Claim Soul Dust'
                        : 'Claim Arena Reward'}
                    </GoldButton>

                    {isClaimed && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: fonts.title,
                          color: COL.goldDim,
                        }}
                      >
                        Reward added to your account.
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <GhostButton
                    size="small"
                    onClick={handleClose}
                    sx={{ mt: 0.5 }}
                  >
                    Close
                  </GhostButton>
                )}
              </Box>
            );
          })}
        </DialogContent>
      </StyledDialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            fontSize: '1.1rem',
            textAlign: 'center',
            px: 4,
            py: 1.5,
            backgroundColor: COL.panel,
            color: COL.text,
            borderRadius: 2,
            border: `1px solid ${COL.goldDim}`,
            boxShadow: glow(0.25),
            fontFamily: fonts.title,
          },
        }}
        message={snackbarMessage}
      />
    </>
  );
}
